import {
  QueryClient,
  QueryKey,
  useMutation as useReactMutation,
  UseMutationOptions as UseReactMutationOptions,
  UseMutationResult as UseReactMutationResult,
  useQuery as useReactQuery,
  UseQueryOptions as UseReactQueryOptions,
  UseQueryResult as UseReactQueryResult,
} from '@tanstack/react-query';
import Axios, { AxiosError, AxiosInstance, AxiosResponse, CancelTokenSource } from 'axios';
import { pick } from 'lodash';

import { getBaseUrl, getBaseUrlConfig } from '@core/base-url';
import { showNotification } from '@shared/components/notification';
import { IndexResponse, ListResponsePagination } from '@shared/types/services';
import { forStringRecord, getDefaultError, getQueriesAsSearch } from '@shared/utils/common';
import { getConfig } from '@shared/utils/window';

const baseUrlConfig = getBaseUrlConfig();

interface TraceItem {
  file: string;
  line: number;
  function: string;
  class: string;
  type: string;
}

export interface ErrorWithTrace {
  exception: string;
  file: string;
  line: number;
  message: string;
  trace: TraceItem[];
}

export interface MessageAndErrors {
  errors: string[];
  message: string;
}

interface ErrorWithStatus {
  detail: string;
  status: number;
}

export interface MessageAndErrorsWithStatus {
  errors: ErrorWithStatus[];
  message: string;
}

export interface MessageAndGroupedErrors {
  errors: Record<string, string[]>;
  message: string;
}

export type AnyErrorResponse = ErrorWithTrace | MessageAndErrors | MessageAndGroupedErrors | MessageAndErrorsWithStatus;

export const defaultPagination: ListResponsePagination = {
  count: 0,
  current_page: 1,
  from: 0,
  to: 0,
  links: {
    last: '',
    next: '',
  },
  per_page: 0,
  total: 0,
  total_pages: 0,
  last_page: 0,
};

export type SuccessResponse<T = any> = AxiosResponse<T>;

export interface FailureResponse<T = any> extends AxiosError<T> {
  errorMessages: string[];
}

export interface YieldResponse<T1 = any, T2 = any> {
  response?: SuccessResponse<T1>;
  error?: FailureResponse<T2>;
}

export type YieldSuccessResponse = (response: AxiosResponse<any>) => YieldResponse;

export type YieldFailureResponse = (error: AxiosError<any>) => YieldResponse;

export const yieldSuccessResponse: YieldSuccessResponse = (response) => {
  return { response } as YieldResponse;
};

export const yieldFailureResponse: YieldFailureResponse = (error) => {
  return { error } as YieldResponse;
};

export const getHTTPClient = (baseURL: string): AxiosInstance => {
  const instance = Axios.create();
  instance.defaults.baseURL = baseURL;

  instance.interceptors.request.use((config) => ({
    ...config,
    withCredentials: true,
    headers: {
      ...config.headers,
      ...(!!getConfig()?.coogan && { 'X-COOGAN': true }),
    },
  }));

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const { status, data } = error.response;

      // Not authorised --> logout
      if (status === 401) {
        const params: Query = {
          redirect: window.location.href,
        };
        if (data.accountLevel) {
          params.accountLevel = data.accountLevel;
        }

        const path = `${getBaseUrl()}logout${getQueriesAsSearch(params, true)}`;
        window.location.href = path;
      }

      // Not accepted terms --> terms
      if (status === 412) {
        window.location.href = `${baseUrlConfig.agencyRoot}/terms`;
      }

      // Service unavailable --> show maintenance page
      if (status === 503) {
        window.location.href = `${baseUrlConfig.agencyRoot}/`;
      }

      if (status === -1) {
        if (error?.response?.config?.timeout) {
          return;
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

const getExternalHTTPClient = (baseUrl: string): AxiosInstance => {
  const instance = Axios.create();
  instance.defaults.baseURL = baseUrl;

  return instance;
};

export const getHTTPAtlasClient = (baseURL: string): AxiosInstance => {
  const instance = Axios.create();
  instance.defaults.baseURL = baseURL;

  instance.interceptors.request.use((config) => ({
    ...config,
    withCredentials: true,
    headers: {
      ...config.headers,
      Authorization: `Bearer ${getConfig()?.atlasJwtToken}`,
      ...(!!getConfig()?.coogan && { 'X-COOGAN': true }),
    },
  }));

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const { status } = error.response;

      if (status === 401) {
        const refreshClient = getHTTPClient(baseUrlConfig.atlasRoot);
        return refreshClient
          .post<{ token: string }>('/jwt/refresh')
          .then((refreshResponse) => {
            // Token refreshed successfully, update cache and retry the original request
            if (refreshResponse.data?.token) {
              window.AS.atlasJwtToken = refreshResponse.data.token;

              // Retry the original failed request with new token
              const originalConfig = error.config;
              originalConfig.headers.Bearer = refreshResponse.data.token;

              return instance(originalConfig);
            }
          })
          .catch(() => {
            window.location.href = `${getBaseUrl()}logout`;
          });
      }

      // Not accepted terms --> terms
      if (status === 412) {
        window.location.href = `${baseUrlConfig.agencyRoot}/terms`;
      }

      // Service unavailable --> show maintenance page
      if (status === 503) {
        window.location.href = `${baseUrlConfig.agencyRoot}/`;
      }

      if (status === -1) {
        if (error?.response?.config?.timeout) {
          return;
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

export const getCancelRequestSource = (): CancelTokenSource => Axios.CancelToken.source(); // TODO remove deprecated method after Axios update

export const getDefaultIndexResponse = <TD = unknown>(): IndexResponse<TD> => ({
  data: [],
  meta: {
    pagination: defaultPagination,
  },
});

// Use Mutation
export type UseMutationOptions<
  TVariables = unknown,
  TData = unknown,
  TError = unknown,
  TContext = unknown
> = UseReactMutationOptions<TData, AxiosError<TError>, TVariables, TContext>;

export const useMutation = <TVariables = unknown, TData = unknown, TError = unknown, TContext = unknown>(
  options: UseReactMutationOptions<TData, AxiosError<TError>, TVariables, TContext>
): UseReactMutationResult<TData, AxiosError<TError>, TVariables, TContext> =>
  useReactMutation<TData, AxiosError<TError>, TVariables, TContext>(options);

// Use Query
export type UseQueryOptions<TData = unknown, TError = unknown, TQueryFnData = TData> = UseReactQueryOptions<
  TQueryFnData,
  AxiosError<TError>,
  TData,
  any
>;

export type QueryFactory<T = unknown> = (payload: any) => UseQueryOptions<T>;

/**
 * @deprecated This wrapper for useQuery is deprecated and should not be used.
 * Please import { useQuery } from '@tanstack/react-query' instead.
 */
export const useQuery = <TQK extends QueryKey = QueryKey, TData = unknown, TError = unknown, TQueryFnData = TData>(
  options: UseReactQueryOptions<TQueryFnData, AxiosError<TError>, TData, TQK>
): UseReactQueryResult<TData, AxiosError<TError>> =>
  useReactQuery<TQueryFnData, AxiosError<TError>, TData, TQK>(options);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      onError: () => showNotification(getDefaultError(), 'error'),

      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
      onError: () => showNotification(getDefaultError(), 'error'),
    },
  },
});

// ---- Core Utils ----

export const cancelQueries = (options: UseQueryOptions) => queryClient.cancelQueries(pick(options, ['queryKey']));

export const getErrorsFromResponse = (error: AxiosError<AnyErrorResponse>): string[] => {
  // console.log('getErrorsFromResponse. response: ', data.response);

  if (!error.response) {
    return [];
  }

  const data = error.response.data;

  const mandatoryFieldsErrors: string[] = [];
  if ('trace' in data) {
    return [data.message];
  } else if (Array.isArray(data.errors)) {
    // console.log('getErrorsFromResponse. processing array...');
    data.errors.forEach((item) => {
      let message = '';

      if (typeof item === 'string') {
        // Type: string
        message = item;
        // console.log('getErrorsFromResponse. string item: ', item);
      } else if (typeof item === 'object') {
        // Type: ErrorWithStatus
        message = item.detail;
        // console.log('getErrorsFromResponse. ErrorWithStatus item: ', item);
      }

      if (message && !mandatoryFieldsErrors.includes(message)) {
        mandatoryFieldsErrors.push(message);
      }
    });

    return mandatoryFieldsErrors;
  } else if (data.errors && Object.keys(data.errors).length) {
    // console.log('data.response.data.errors: ', data.response.data.errors);
    forStringRecord(data.errors, (errors) => {
      // console.log('getErrorsFromResponse. forStringRecord errors: ', errors);
      errors.forEach((message) => {
        if (message && !mandatoryFieldsErrors.includes(message)) {
          mandatoryFieldsErrors.push(message);
        }
      });
    });

    return mandatoryFieldsErrors;
  } else if (data.message) {
    return [data.message];
  }

  return [];
};

export type UpdateCacheCallback<T = unknown> = (cache: T) => T;

export const updateQueryCache = <TD = unknown>(queryKey: QueryKey, callback: UpdateCacheCallback<TD>) =>
  queryClient.setQueryData<TD>(queryKey, (cache) => (cache ? callback(cache) : cache));

// ---- Paginated Index Utils ----

export const legacyUpdateIndexQueryItem = <TD extends ItemWithId = ItemWithId, TI extends Id = Id>(
  queryFactory: QueryFactory<IndexResponse<TD>>,
  id: TI,
  data: TD,
  params?: Query
) => {
  queryClient.setQueryData<IndexResponse<TD>>(queryFactory({ query: { ...params } }).queryKey, (indexResponse) => {
    if (indexResponse) {
      return {
        ...indexResponse,
        data: indexResponse.data.map((item) => (item.id === id ? data : item)),
      };
    }

    return indexResponse;
  });
};

export const updatePaginatedIndexQueryItems = <TD extends ItemWithId = ItemWithId>(
  queryOptions: UseQueryOptions<IndexResponse<TD>>,
  updateCb: (item: TD) => TD
): void => {
  queryClient.setQueriesData<IndexResponse<TD> | undefined>(
    { exact: false, queryKey: queryOptions.queryKey },
    (response) => (response ? { ...response, data: response.data.slice().map(updateCb) } : undefined)
  );
};

export const clearIndexQuery = <TD extends ItemWithId = ItemWithId>(
  queryFactory: QueryFactory<IndexResponse<TD>>,
  params?: Query
) => {
  queryClient.setQueryData<IndexResponse<TD>>(queryFactory({ query: { ...params } }).queryKey, () =>
    getDefaultIndexResponse()
  );
};

export const deleteIndexQueryItem = <TD extends ItemWithId = ItemWithId, TI extends Id = Id>(
  queryFactory: QueryFactory<IndexResponse<TD>>,
  id: TI,
  params?: Query
) => {
  queryClient.setQueryData<IndexResponse<TD>>(queryFactory({ query: { ...params } }).queryKey, (indexResponse) => {
    if (indexResponse) {
      return {
        ...indexResponse,
        data: indexResponse.data.filter((item) => item.id !== id),
      };
    }

    return indexResponse;
  });
};

// ---- Index Utils ----

export const addIndexQueryItems = <TD extends ItemWithId = ItemWithId>(
  queryOptions: UseQueryOptions<TD[]>,
  items: TD[],
  reverse = false
): void => {
  queryClient.setQueriesData<TD[] | undefined>({ exact: false, queryKey: queryOptions.queryKey }, (data) => {
    if (data) {
      return reverse ? [...items, ...data] : [...data, ...items];
    }

    return [...items];
  });
};

export const removeIndexQueryItems = <TD extends ItemWithId = ItemWithId>(
  queryOptions: UseQueryOptions<TD[]>,
  filterCb: (item: TD) => boolean
): void => {
  queryClient.setQueriesData<TD[] | undefined>({ exact: false, queryKey: queryOptions.queryKey }, (data) =>
    data ? data.slice().filter(filterCb) : undefined
  );
};

export const updateIndexQueryItems = <TD extends ItemWithId = ItemWithId>(
  queryOptions: UseQueryOptions<TD[]>,
  updateCb: (item: TD) => TD
): void => {
  queryClient.setQueriesData<TD[] | undefined>({ exact: false, queryKey: queryOptions.queryKey }, (data) =>
    data ? data.slice().map(updateCb) : undefined
  );
};

// ---- Show Utils ----

export const updateQueryItem = <TD extends ItemWithId = ItemWithId>(
  queryOptions: UseQueryOptions<TD>,
  itemId: TD['id'],
  updateCb: (item: TD) => TD
): void => {
  queryClient.setQueriesData<TD | undefined>({ exact: false, queryKey: queryOptions.queryKey }, (item) => {
    if (!item) {
      return item;
    }

    return item.id === itemId ? updateCb({ ...item }) : item;
  });
};

export const updateQueryItemAndGetPrevious = <TD extends ItemWithId = ItemWithId>(
  queryOptions: UseQueryOptions<TD>,
  itemId: TD['id'],
  updateCb: (item: TD) => TD
): TD | undefined => {
  const previous = queryClient.getQueryData<TD | undefined>(queryOptions.queryKey);

  updateQueryItem(queryOptions, itemId, updateCb);

  return previous;
};

// ---- Instances ----

/**
 * HTTP client for the agency app endpoints.
 */
export const $httpRoot = getHTTPClient(baseUrlConfig.agencyRoot);

/**
 * HTTP client for the agency API endpoints.
 */
export const $httpRootApi = getHTTPClient(baseUrlConfig.agencyApi);

/**
 * HTTP client for the landlord app endpoints.
 */
export const $httpLandlord = getHTTPClient(baseUrlConfig.landlordRoot);

/**
 * HTTP client for the landlord API endpoints.
 */
export const $httpLandlordApi = getHTTPClient(baseUrlConfig.landlordApi);
/**
 * HTTP client for the landlord app endpoints.
 */
export const $httpAtlas = getHTTPClient(baseUrlConfig.atlasRoot);

/**
 * HTTP client for endpoints common between apps.
 */
export const $httpCommon = getHTTPClient(baseUrlConfig.commonRoot);

/**
 * HTTP client for API endpoints common between apps.
 */
export const $httpCommonApi = getHTTPClient(baseUrlConfig.commonApi);

/**
 * HTTP client for Postcode Anywhere API
 */
export const $httpPca = getExternalHTTPClient('https://services.postcodeanywhere.co.uk/capture/Interactive');

/**
 * HTTP client for the developer portal endpoints.
 */
export const $httpDeveloper = getHTTPClient(baseUrlConfig.developerRoot);

/**
 * HTTP client for the developer portal API endpoints.
 */
export const $httpDeveloperApi = getHTTPClient(baseUrlConfig.developerApi);